import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, TextField, InputAdornment, IconButton,
  Tooltip, MenuItem, Select, FormControl, InputLabel, Drawer, Divider, Button,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  ManageHistoryRounded, Search as SearchIcon, Clear as ClearIcon,
  Close as CloseIcon, OpenInNew, Refresh, Download, ContentCopy,
  CleaningServicesOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { dataGridSx, dataGridContainerSx } from '../utils/tableStyles';
// `logAudit` is intentionally NOT imported here. The PM2 log flush is the
// only action this page would write, and writing it kept failing against
// the deployed worker. The flush is recorded via the backend's `[API]
// Manual PM2 log flush triggered` log line instead.

// Mirrors the worker's closed-set enum so the filter dropdown stays honest.
const ACTION_VALUES = [
  'deploy', 'deploy_batch', 'retry', 'cancel', 'reconcile_stale',
  'schedule_create', 'schedule_cancel', 'schedule_fire',
  'publish_release_note', 'save_release_note_draft', 'delete_release_note',
  'toggle_notifications',
] as const;
type AuditAction = typeof ACTION_VALUES[number];

// `DisplayAuditAction` extends `AuditAction` with the synthetic PM2 flush
// value used only for in-grid rendering. Kept separate from `ACTION_VALUES`
// so the filter dropdown doesn't expose a value the worker would reject.
type DisplayAuditAction = AuditAction | 'pm2_log_flush';

const TARGET_TYPE_VALUES = [
  'build', 'pipeline', 'app', 'release_note', 'schedule', 'batch', 'session',
] as const;
type AuditTargetType = typeof TARGET_TYPE_VALUES[number];

interface AuditRow {
  id: number;
  actor: string;
  action: DisplayAuditAction;
  targetType: AuditTargetType;
  targetId: string;
  metadata: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

const ACTION_META: Record<DisplayAuditAction, { label: string; color: string; bg: string }> = {
  deploy:                     { label: 'Deploy',           color: '#15803d', bg: 'rgba(22, 163, 74, 0.12)' },
  deploy_batch:               { label: 'Deploy (batch)',   color: '#15803d', bg: 'rgba(22, 163, 74, 0.12)' },
  retry:                      { label: 'Retry',            color: '#1d4ed8', bg: 'rgba(59, 130, 246, 0.12)' },
  cancel:                     { label: 'Cancel',           color: '#b91c1c', bg: 'rgba(220, 38, 38, 0.12)' },
  reconcile_stale:            { label: 'Reconcile stale',  color: '#92400e', bg: 'rgba(245, 158, 11, 0.15)' },
  schedule_create:            { label: 'Schedule created', color: '#4338ca', bg: 'rgba(99, 102, 241, 0.12)' },
  schedule_cancel:            { label: 'Schedule cancelled', color: '#b45309', bg: 'rgba(245, 158, 11, 0.15)' },
  schedule_fire:              { label: 'Schedule fired',   color: '#4338ca', bg: 'rgba(99, 102, 241, 0.12)' },
  publish_release_note:       { label: 'Note published',   color: '#15803d', bg: 'rgba(22, 163, 74, 0.12)' },
  save_release_note_draft:    { label: 'Note draft saved', color: '#475569', bg: 'rgba(15, 23, 42, 0.08)' },
  delete_release_note:        { label: 'Note deleted',     color: '#b91c1c', bg: 'rgba(220, 38, 38, 0.12)' },
  toggle_notifications:       { label: 'Notifications',    color: '#475569', bg: 'rgba(15, 23, 42, 0.08)' },
  // Client-side-only chip — see the comment on AuditAction in utils/audit.ts.
  pm2_log_flush:              { label: 'PM2 log flush',    color: '#92400e', bg: 'rgba(245, 158, 11, 0.15)' },
};

// PM2 log flushes are recorded on the BACKEND in Redis (the worker's
// audit endpoint refused both `pm2_log_flush` and `reconcile_stale`-with-
// metadata-kind payloads) and surfaced here by fetching
// /api/logs/flush-history and merging into the displayed rows. They use
// the same AuditRow shape so the table render code doesn't need to
// branch — only the action chip gets a distinct label / colour below.

interface AuditLogComponentProps { isActive: boolean }

export const AuditLogComponent: React.FC<AuditLogComponentProps> = ({ isActive }) => {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<AuditRow | null>(null);

  const [actor, setActor] = useState('');
  const [action, setAction] = useState<'' | AuditAction>('');
  const [targetType, setTargetType] = useState<'' | AuditTargetType>('');
  const [search, setSearch] = useState('');
  const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
  const [snack, setSnack] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>({ open: false, msg: '', severity: 'info' });

  // PM2 flush history fetched from the backend's Redis ring. Kept separate
  // from the worker-sourced `rows` so we can re-fetch independently after
  // a manual flush (no need to invalidate the whole audit page just because
  // the operator triggered cleanup).
  const [flushHistoryRows, setFlushHistoryRows] = useState<AuditRow[]>([]);
  const fetchFlushHistory = async () => {
    try {
      const tok = localStorage.getItem('token');
      const resp = await axios.get('/api/logs/flush-history?limit=200', {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const items = Array.isArray(resp.data?.items) ? resp.data.items : [];
      // Map the backend's PM2 flush entries onto the AuditRow shape the
      // DataGrid already understands. id is a negative number so it
      // can't ever collide with a worker audit row's autoincrement id.
      const mapped: AuditRow[] = items.map((e: any, idx: number) => ({
        id: -1 - idx,
        actor: e.actor || 'unknown',
        action: 'pm2_log_flush',
        targetType: 'session',
        targetId: e.id || e.createdAt || String(idx),
        metadata: {
          pm2FlushOk: e.pm2FlushOk,
          totalFreedBytes: e.totalFreedBytes,
          truncatedFiles: e.truncatedFiles,
          message: e.message,
        },
        ipAddress: e.ipAddress ?? null,
        userAgent: e.userAgent ?? null,
        createdAt: e.createdAt,
      }));
      setFlushHistoryRows(mapped);
    } catch (err) {
      // Don't fail the audit page if the history endpoint is unavailable
      // — the worker audit rows are still useful on their own.
      console.warn('[audit] flush-history fetch failed (non-fatal):', err);
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (actor.trim()) params.append('actor', actor.trim());
      if (action) params.append('action', action);
      if (targetType) params.append('targetType', targetType);
      params.append('limit', String(pageModel.pageSize));
      params.append('offset', String(pageModel.page * pageModel.pageSize));
      const tok = localStorage.getItem('token');
      const resp = await axios.get(`/api/audit?${params.toString()}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      setRows(resp.data?.items || []);
      setTotal(resp.data?.metadata?.total ?? 0);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, action, targetType, pageModel.page, pageModel.pageSize]);

  // Flush history is independent of the worker pagination state — fetch it
  // on activation and after a manual flush only.
  useEffect(() => {
    if (!isActive) return;
    fetchFlushHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // The actor + free-text filters fire on debounce so the user can type
  // without thrashing the worker. The other two filter changes refetch
  // immediately (above).
  useEffect(() => {
    if (!isActive) return;
    const t = setTimeout(() => fetchRows(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor]);

  // Merge worker audit rows with locally-stored PM2 flush rows. PM2 flushes
  // are only shown on page 0 with no action filter — that keeps the
  // server-side pagination behaviour intact while still surfacing the
  // most recent flushes where operators expect to see them. Sorted by
  // createdAt DESC so brand-new flushes show up at the very top.
  const mergedRows = useMemo<AuditRow[]>(() => {
    const showFlushes = pageModel.page === 0 && !action && (!targetType || targetType === 'session');
    if (!showFlushes) return rows;
    const combined = [...flushHistoryRows, ...rows];
    combined.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return combined;
  }, [rows, flushHistoryRows, pageModel.page, action, targetType]);

  // Free-text search runs client-side over the loaded page — it spans
  // metadata JSON, target id, and IP. Server-side full-text on a JSON blob
  // would need indexed views; client-side is fine for the page sizes we use.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mergedRows;
    return mergedRows.filter((r) =>
      r.actor.toLowerCase().includes(q) ||
      r.action.toLowerCase().includes(q) ||
      r.targetType.toLowerCase().includes(q) ||
      r.targetId.toLowerCase().includes(q) ||
      (r.ipAddress || '').toLowerCase().includes(q) ||
      JSON.stringify(r.metadata || {}).toLowerCase().includes(q)
    );
  }, [rows, search]);

  // ---------------- Manual PM2 log flush ----------------
  // Mirrors the monthly cron at the backend (see log-cleanup.service.ts) but
  // fires on demand from the Audit Log page so admins can free disk
  // immediately when the dev VM is filling up — no SSH required.
  const [flushDialogOpen, setFlushDialogOpen] = useState(false);
  const [flushing, setFlushing] = useState(false);
  const triggerPm2Flush = async () => {
    setFlushing(true);
    try {
      const tok = localStorage.getItem('token');
      const resp = await axios.post('/api/logs/flush-pm2', {}, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const data = resp.data || {};
      const freedBytes: number = data.totalFreedBytes ?? 0;
      const truncatedFiles: Array<{ path: string; freedBytes?: number; error?: string }> =
        data.truncatedFiles || [];
      const failures = truncatedFiles.filter(f => f.error);
      const summary = data.message || `Truncated ${truncatedFiles.length} file(s); freed ${formatBytes(freedBytes)}.`;

      // No audit-log write for this action. The PM2 flush only touches
      // three local log files on the dev VM; recording it in the worker's
      // audit table kept failing (the deployed worker's enum doesn't
      // accept the action / target shape) and the failures were noise.
      // The backend still emits `[API] Manual PM2 log flush triggered`
      // which is the breadcrumb ops needs.
      const snackMsg = failures.length > 0
        ? `${summary} — ${failures.length} file(s) couldn't be cleared (see backend logs).`
        : summary;
      const snackSeverity: 'success' | 'info' = failures.length > 0 ? 'info' : 'success';
      setSnack({ open: true, msg: snackMsg, severity: snackSeverity });
      setFlushDialogOpen(false);
      // Refresh the PM2 flush history so the new event appears at the top
      // of the audit grid immediately. We don't refresh the worker rows
      // because the flush isn't recorded there.
      fetchFlushHistory();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'PM2 flush failed.';
      setSnack({ open: true, msg: `PM2 flush failed: ${message}`, severity: 'error' });
    } finally {
      setFlushing(false);
    }
  };

  const exportCsv = () => {
    const header = ['id', 'createdAt', 'actor', 'action', 'targetType', 'targetId', 'ipAddress', 'metadata'];
    const lines = [header.join(',')];
    for (const r of filteredRows) {
      const cells = [
        String(r.id),
        new Date(r.createdAt).toISOString(),
        csvSafe(r.actor),
        r.action,
        r.targetType,
        csvSafe(r.targetId),
        csvSafe(r.ipAddress || ''),
        csvSafe(r.metadata ? JSON.stringify(r.metadata) : ''),
      ];
      lines.push(cells.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${format(new Date(), 'yyyyMMdd-HHmm')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'When',
      width: 170,
      renderCell: (p: GridRenderCellParams) => {
        const d = new Date((p.row as AuditRow).createdAt);
        if (Number.isNaN(d.getTime())) return <Typography variant="caption" color="text.disabled">—</Typography>;
        return (
          <Stack direction="column" sx={{ lineHeight: 1.2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{format(d, 'MMM dd, HH:mm:ss')}</Typography>
            <Typography variant="caption" color="text.secondary">{format(d, 'yyyy')}</Typography>
          </Stack>
        );
      },
    },
    {
      field: 'actor',
      headerName: 'Actor',
      width: 220,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(p.row as AuditRow).actor}
        </Typography>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 180,
      renderCell: (p: GridRenderCellParams) => {
        const a = (p.row as AuditRow).action;
        const m = ACTION_META[a] || { label: a, color: '#475569', bg: 'rgba(15, 23, 42, 0.08)' };
        return <Chip size="small" label={m.label} sx={{ fontWeight: 700, bgcolor: m.bg, color: m.color }} />;
      },
    },
    {
      field: 'target',
      headerName: 'Target',
      flex: 1,
      minWidth: 240,
      renderCell: (p: GridRenderCellParams) => {
        const r = p.row as AuditRow;
        const md = r.metadata || {};
        const hint = (md as any).composerHost || (md as any).version || (md as any).title || '';
        return (
          <Stack direction="column" sx={{ lineHeight: 1.2, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
              {r.targetType} · {r.targetId}
            </Typography>
            {hint && (
              <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {String(hint)}
              </Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'ipAddress',
      headerName: 'IP',
      width: 140,
      renderCell: (p: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
          {(p.row as AuditRow).ipAddress || '—'}
        </Typography>
      ),
    },
    {
      field: 'details',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (p: GridRenderCellParams) => (
        <Tooltip title="Inspect full event" arrow>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDrawer(p.row as AuditRow); }}>
            <OpenInNew sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' }}>
            <ManageHistoryRounded />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Operator Audit Log</Typography>
            <Typography variant="body2" color="text.secondary">
              Append-only stream of every meaningful action the dashboard performs. Stamped server-side with the OIDC user's email.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh" arrow>
            <span>
              <IconButton onClick={fetchRows} disabled={loading} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <Refresh fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Export filtered rows as CSV" arrow>
            <span>
              <IconButton data-tour="audit-export" onClick={exportCsv} disabled={filteredRows.length === 0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <Download fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {/* Manual PM2 / daemon log flush. Runs the same path as the
              monthly cron — just on demand. Visually labelled like a real
              button (not just an icon) so the destructive intent is clear. */}
          <Tooltip title="Run the PM2 log cleanup now — same path as the monthly cron. Clears mobapp-mig-out.log, mobapp-mig-error.log, and /root/.pm2/pm2.log." arrow>
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setFlushDialogOpen(true)}
                startIcon={<CleaningServicesOutlined fontSize="small" />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'divider',
                  color: '#92400e',
                  bgcolor: 'rgba(245, 158, 11, 0.06)',
                  '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.12)', borderColor: '#f59e0b' },
                }}
              >
                Flush PM2 logs
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Confirm dialog for the destructive flush. Lists exactly which
          files are about to be cleared so the operator knows what they're
          authorising. */}
      <Dialog
        open={flushDialogOpen}
        onClose={() => !flushing && setFlushDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Flush PM2 logs?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body2">
              This truncates the dashboard's three log files in place — nothing else:
            </Typography>
            <Box
              component="ul"
              sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 }, '& code': { fontFamily: 'monospace', fontSize: '0.78rem', bgcolor: '#f1f5f9', px: 0.5, borderRadius: 0.5 } }}
            >
              <li>Truncates <code>/root/.pm2/pm2.log</code> (the PM2 daemon log)</li>
              <li>Truncates <code>mobapp-mig-out.log</code></li>
              <li>Truncates <code>mobapp-mig-error.log</code></li>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Trims these files in place (PM2's open file descriptors keep writing) — no blanket <code>pm2 flush</code>, so other processes' logs are untouched. Safe to run any time — no service restart, no data loss beyond historical log lines.
            </Typography>
            <Alert severity="info" sx={{ py: 0.5 }}>
              The action is recorded in this audit log as <strong>PM2 log flush</strong> with the actor + per-file breakdown + freed bytes in its metadata. (Stored separately from the worker audit, in the backend's Redis ring — capped at the last 200 events.)
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setFlushDialogOpen(false)} disabled={flushing}>
            Cancel
          </Button>
          <Button
            onClick={triggerPm2Flush}
            disabled={flushing}
            variant="contained"
            startIcon={flushing ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <CleaningServicesOutlined />}
            sx={{ bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}
          >
            {flushing ? 'Flushing…' : 'Flush now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            data-tour="audit-filter"
            size="small"
            placeholder="Filter by actor email…"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
              endAdornment: actor && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setActor('')}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="action-filter">Action</InputLabel>
            <Select
              labelId="action-filter"
              label="Action"
              value={action}
              onChange={(e) => setAction(e.target.value as any)}
            >
              <MenuItem value="">All actions</MenuItem>
              {ACTION_VALUES.map(a => (
                <MenuItem key={a} value={a}>{ACTION_META[a].label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="target-filter">Target</InputLabel>
            <Select
              labelId="target-filter"
              label="Target"
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as any)}
            >
              <MenuItem value="">All targets</MenuItem>
              {TARGET_TYPE_VALUES.map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <TextField
            size="small"
            placeholder="Search loaded rows (metadata, target id, IP)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 320 }}
          />
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={dataGridContainerSx}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          paginationModel={pageModel}
          onPaginationModelChange={setPageModel}
          pageSizeOptions={[25, 50, 100]}
          rowHeight={64}
          disableRowSelectionOnClick
          onRowClick={(p) => setDrawer(p.row as AuditRow)}
          sx={dataGridSx}
        />
      </Box>

      <Drawer
        anchor="right"
        open={!!drawer}
        onClose={() => setDrawer(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 540 } } }}
      >
        {drawer && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff' }}>
                  <ManageHistoryRounded sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {ACTION_META[drawer.action]?.label || drawer.action}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(drawer.createdAt), 'PPpp')} · #{drawer.id}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={() => setDrawer(null)} size="small"><CloseIcon /></IconButton>
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Field label="Actor" value={drawer.actor} mono />
              <Field label="Action" value={drawer.action} mono />
              <Field label="Target" value={`${drawer.targetType} · ${drawer.targetId}`} mono />
              <Field label="IP address" value={drawer.ipAddress || '—'} mono />
              <Field label="User agent" value={drawer.userAgent || '—'} small />
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>Metadata</Typography>
                <Tooltip title="Copy JSON">
                  <IconButton size="small" onClick={() => {
                    navigator.clipboard?.writeText(JSON.stringify(drawer.metadata || {}, null, 2))
                      .then(() => setSnack({ open: true, msg: 'Metadata copied.', severity: 'success' }))
                      .catch(() => setSnack({ open: true, msg: 'Copy failed.', severity: 'error' }));
                  }}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Box sx={{
                bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 2, p: 2,
                fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.5,
                maxHeight: 360, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {drawer.metadata ? JSON.stringify(drawer.metadata, null, 2) : '— no metadata —'}
              </Box>
            </Box>
            <Divider />
            <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setDrawer(null)}>Close</Button>
            </Box>
          </Box>
        )}
      </Drawer>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const Field: React.FC<{ label: string; value: string; mono?: boolean; small?: boolean }> = ({ label, value, mono, small }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.25 }}>{label}</Typography>
    <Typography
      variant={small ? 'caption' : 'body2'}
      sx={{ fontWeight: 600, fontFamily: mono ? 'monospace' : 'inherit', overflowWrap: 'anywhere' }}
    >
      {value}
    </Typography>
  </Box>
);

const csvSafe = (s: string) => {
  // Defuse Excel/Sheets formula injection (=, +, -, @, tab, CR) and quote-escape.
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
};

// Same shape as the backend's formatBytes helper. Kept local so the
// component can render byte totals from the PM2 flush response without
// pulling in an extra util module.
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export default AuditLogComponent;
